/**
 * PublishingHistoryPage — Patch 33.2.1.
 * Provider-neutral history view. Reads local publishing_jobs only.
 */
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Filter, Loader2, XCircle } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";

import {
  LAUNCH_PLATFORMS,
  PublishingChannelsAPI,
  type HistoryRow,
} from "@/entities/channels";
import { useCurrentWorkspace } from "@/entities/workspace";

const STATUS_TONE: Record<string, "success" | "warn" | "danger" | "muted"> = {
  published: "success",
  scheduled: "muted",
  publishing: "muted",
  draft: "muted",
  partial: "warn",
  failed: "danger",
  cancelled: "muted",
  succeeded: "success",
  dead_letter: "danger",
  queued: "muted",
  running: "muted",
};

function toneClass(tone: "success" | "warn" | "danger" | "muted"): string {
  switch (tone) {
    case "success":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
    case "warn":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30";
    case "danger":
      return "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30";
    case "muted":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export default function PublishingHistoryPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [since, setSince] = useState<string>("");

  const filter = useMemo(
    () => ({
      status: statusFilter === "all" ? undefined : statusFilter,
      platform: platformFilter === "all" ? undefined : platformFilter,
      since: since || undefined,
      limit: 100,
    }),
    [statusFilter, platformFilter, since],
  );

  const { data, isLoading, isError } = useQuery<{ items: HistoryRow[]; total: number }>({
    queryKey: ["publishing", "history", workspaceId, filter],
    queryFn: () => PublishingChannelsAPI.history(workspaceId!, filter),
    enabled: !!workspaceId,
  });

  if (!workspaceId) {
    return <div className="p-6 text-muted-foreground">Select a workspace.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="publishing-history-page">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Publishing History</h1>
        <p className="text-sm text-muted-foreground">
          Every draft, scheduled, and published post from this workspace.
        </p>
      </header>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-4">
          <div>
            <Label htmlFor="hist-status">
              <Filter className="mr-1 inline h-3 w-3" /> Status
            </Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger id="hist-status" data-testid="history-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="succeeded">Succeeded</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="dead_letter">Dead-letter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="hist-platform">Platform</Label>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger id="hist-platform" data-testid="history-filter-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {LAUNCH_PLATFORMS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="hist-since">Since</Label>
            <Input
              id="hist-since"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              data-testid="history-filter-since"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full text-right text-xs text-muted-foreground">
              {data ? `${data.total} record${data.total === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : isError ? (
        <Card className="border-rose-500/40 bg-rose-500/5">
          <CardContent className="p-4 text-sm text-rose-700 dark:text-rose-300">
            Unable to load publishing history. Please retry.
          </CardContent>
        </Card>
      ) : (data?.items ?? []).length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="history-empty">
            No publishing activity yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data!.items.map((r) => {
            const tone = STATUS_TONE[r.status] ?? "muted";
            const meta = LAUNCH_PLATFORMS.find((p) => p.key === r.channel);
            return (
              <Card key={r.id} data-testid={`history-row-${r.id}`}>
                <CardContent className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-6">
                  <div className="sm:col-span-1">
                    <div className="text-xs text-muted-foreground">Platform</div>
                    <div className="flex items-center gap-2 font-medium">
                      {meta ? (
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: meta.color }}
                          aria-hidden="true"
                        />
                      ) : null}
                      {meta?.label ?? r.channel ?? "—"}
                    </div>
                  </div>
                  <div className="sm:col-span-2 min-w-0">
                    <div className="text-xs text-muted-foreground">Preview</div>
                    <div className="truncate text-sm">{r.content_preview ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Status</div>
                    <Badge variant="outline" className={toneClass(tone)}>
                      {tone === "success" ? (
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                      ) : tone === "danger" ? (
                        <XCircle className="mr-1 h-3 w-3" />
                      ) : null}
                      {r.status}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Scheduled</div>
                    <div className="text-sm">{fmt(r.scheduled_for)}</div>
                  </div>
                  <div className="flex flex-col justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground">Updated</div>
                      <div className="text-sm">{fmt(r.updated_at)}</div>
                    </div>
                    {r.external_url ? (
                      <a
                        href={r.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`history-view-${r.id}`}
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Silence unused import in strict mode when there are zero rows.
void Loader2;
